import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Search,
  Phone,
  Email,
  Person,
  Subject,
  Refresh,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  CalendarToday,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getContacts, clearError } from "../redux/slices/adminContactSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdminContact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const dispatch = useDispatch();
  
  // Safely access state with fallback
  const adminContactState = useSelector((state) => state.adminContact || {});
  const { contacts = [], loading = false, error = null } = adminContactState;
  
  const [searchTerm, setSearchTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  // Show error in toast
  useEffect(() => {
    if (error) {
      toast.error(error || "Failed to fetch contacts", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const fetchContacts = async () => {
    try {
      setIsRefreshing(true);
      const result = await dispatch(getContacts()).unwrap();
    } catch (err) {
      toast.error(err?.message || "Failed to fetch contacts", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter contacts based on search - ensure contacts is an array
  const filteredContacts = Array.isArray(contacts) 
    ? contacts.filter((contact) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          contact.name?.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.phone?.includes(searchTerm) ||
          contact.subject?.toLowerCase().includes(searchLower) ||
          contact.message?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.info(`📋 ${label} copied to clipboard`, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  // Toggle expansion for mobile view
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };


  if (loading && contacts.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          p: 2,
        }}
      >
        <CircularProgress sx={{ color: "var(--primary)" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3, lg: 4 }, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: { xs: "12px", sm: "16px" },
            bgcolor: "#ffffff",
            border: "1px solid var(--border)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 2 },
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                variant={isMobile ? "h6" : "h5"}
                sx={{
                  fontWeight: 700,
                  color: "var(--text)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                Contact Messages
                <Chip
                  label={Array.isArray(contacts) ? contacts.length : 0}
                  size="small"
                  sx={{
                    bgcolor: "var(--primary)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                />
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "var(--text-light)",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  mt: 0.5,
                }}
              >
                Manage all customer inquiries and messages
              </Typography>
            </Box>

            <Box 
              sx={{ 
                display: "flex", 
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 2 }, 
                width: { xs: "100%", sm: "auto" } 
              }}
            >
              <TextField
                placeholder="Search contacts..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 200, md: 250 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    bgcolor: "var(--bg-light)",
                    fontSize: { xs: "0.875rem", sm: "0.9rem" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "var(--text-light)", fontSize: { xs: 20, sm: 24 } }} />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                variant="outlined"
                onClick={fetchContacts}
                disabled={isRefreshing}
                startIcon={<Refresh />}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "12px",
                  borderColor: "var(--primary)",
                  color: "var(--primary)",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                  py: { xs: 1, sm: 0.75 },
                  "&:hover": {
                    borderColor: "var(--primary-dark)",
                    bgcolor: "var(--bg-primary-soft)",
                  },
                }}
              >
                {isRefreshing ? "Loading..." : "Refresh"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: { xs: 2, sm: 2.5, md: 3 }, 
              borderRadius: "12px",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
            onClose={() => dispatch(clearError())}
          >
            {error}
          </Alert>
        )}

        {/* Mobile/Tablet Card View */}
        {(isMobile || isTablet) ? (
          <Stack spacing={2}>
            {filteredContacts.length > 0 ? (
              filteredContacts.map((contact, index) => (
                <Card
                  key={contact._id || index}
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid var(--border)",
                    boxShadow: "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    {/* Header with name and status */}
                    <Box 
                      sx={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "flex-start",
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography 
                          sx={{ 
                            fontWeight: 600, 
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                            color: "var(--text)",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {contact.name || "N/A"}
                          <Chip
                            label={contact.subject || "General"}
                            size="small"
                            sx={{
                              bgcolor: "var(--bg-primary-soft)",
                              color: "var(--primary)",
                              fontWeight: 500,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              height: { xs: 20, sm: 24 },
                            }}
                          />
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: "var(--text-light)",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        >
                          <CalendarToday sx={{ fontSize: { xs: 14, sm: 16 } }} />
                          {formatDate(contact.createdAt || contact.date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(contact._id || index)}
                          sx={{ 
                            color: "var(--text-light)",
                            p: 0.5,
                          }}
                        >
                          {expandedId === (contact._id || index) ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Basic Info */}
                    <Stack spacing={1} sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Email sx={{ fontSize: { xs: 16, sm: 18 }, color: "var(--text-light)" }} />
                        <Typography 
                          sx={{ 
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            color: "var(--text)",
                            wordBreak: "break-all",
                            cursor: "pointer",
                            "&:hover": { color: "var(--primary)" },
                          }}
                          onClick={() => copyToClipboard(contact.email, "Email")}
                        >
                          {contact.email || "N/A"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(contact.email, "Email")}
                          sx={{ p: 0.5 }}
                        >
                          <ContentCopy sx={{ fontSize: { xs: 14, sm: 16 }, color: "var(--text-light)" }} />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Phone sx={{ fontSize: { xs: 16, sm: 18 }, color: "var(--text-light)" }} />
                        <Typography 
                          sx={{ 
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            color: "var(--text)",
                            cursor: "pointer",
                            "&:hover": { color: "var(--primary)" },
                          }}
                          onClick={() => copyToClipboard(contact.phone, "Phone")}
                        >
                          {contact.phone || "N/A"}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(contact.phone, "Phone")}
                          sx={{ p: 0.5 }}
                        >
                          <ContentCopy sx={{ fontSize: { xs: 14, sm: 16 }, color: "var(--text-light)" }} />
                        </IconButton>
                      </Box>
                    </Stack>

                    {/* Expandable Message */}
                    <Collapse in={expandedId === (contact._id || index)} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: "1px solid var(--border)",
                        }}
                      >
                        <Typography 
                          sx={{ 
                            fontSize: { xs: "0.8rem", sm: "0.875rem" },
                            color: "var(--text)",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {contact.message || "No message"}
                        </Typography>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Paper
                sx={{
                  p: { xs: 4, sm: 6 },
                  textAlign: "center",
                  borderRadius: "12px",
                  border: "1px solid var(--border)",
                  bgcolor: "#ffffff",
                }}
              >
                <Typography sx={{ color: "var(--text-light)" }}>
                  {searchTerm ? "No matching contacts found" : "No contacts found"}
                </Typography>
              </Paper>
            )}
          </Stack>
        ) : (
          /* Desktop Table View */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid var(--border)",
              overflow: "auto",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "var(--bg-light)" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Person fontSize="small" /> Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Email fontSize="small" /> Email
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Phone fontSize="small" /> Phone
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Subject fontSize="small" /> Subject
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>Message</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.875rem" }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredContacts.length > 0 ? (
                  filteredContacts.map((contact, index) => (
                    <TableRow
                      key={contact._id || index}
                      sx={{
                        "&:hover": {
                          bgcolor: "var(--bg-light)",
                        },
                        transition: "background 0.2s ease",
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      
                      <TableCell>
                        <Tooltip title="Click to copy name">
                          <Typography
                            sx={{ 
                              fontWeight: 600, 
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              "&:hover": { color: "var(--primary)" }
                            }}
                            onClick={() => copyToClipboard(contact.name, "Name")}
                          >
                            {contact.name || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      
                      <TableCell>
                        <Tooltip title="Click to copy email">
                          <Typography
                            sx={{ 
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              "&:hover": { color: "var(--primary)" }
                            }}
                            onClick={() => copyToClipboard(contact.email, "Email")}
                          >
                            {contact.email || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      
                      <TableCell>
                        <Tooltip title="Click to copy phone">
                          <Typography
                            sx={{ 
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              "&:hover": { color: "var(--primary)" }
                            }}
                            onClick={() => copyToClipboard(contact.phone, "Phone")}
                          >
                            {contact.phone || "N/A"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          label={contact.subject || "General"}
                          size="small"
                          sx={{
                            bgcolor: "var(--bg-primary-soft)",
                            color: "var(--primary)",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.875rem",
                          }}
                          title={contact.message}
                        >
                          {contact.message || "N/A"}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ fontSize: "0.813rem" }}>
                        {formatDate(contact.createdAt || contact.date)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: "var(--text-light)" }}>
                        {searchTerm ? "No matching contacts found" : "No contacts found"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer Stats */}
        {filteredContacts.length > 0 && (
          <Box 
            sx={{ 
              mt: { xs: 2, sm: 2.5, md: 3 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                color: "var(--text-light)",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
              }}
            >
              Showing {filteredContacts.length} of {Array.isArray(contacts) ? contacts.length : 0} contacts
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default AdminContact;